import { useCallback, useMemo } from "react";

import {
  createStripeCheckoutSession,
  getGrowthPlans,
} from "@/api/endpoints/stripe";
import { PLAN_TYPE, PlanType } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { basic, custom1, custom2 } from "@/mappings/growth_plans";
import { cx } from "@/utils";
import Modal, { ModalContent } from "@components/Modal";
import Typography from "@components/Typography";
import * as Sentry from "@sentry/browser";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import * as PlanCard from "./PlanCard";

import styles from "./GrowthPlanModalAE.module.css";

const usePlanData = () => {
  return useQuery({
    queryKey: ["growthPlans"],
    queryFn: getGrowthPlans,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  });
};

const useStripeGrowthPlanUpgrade = (planType: PlanType) => {
  const { currency } = useI18n();
  const { isLoading, data } = usePlanData();

  const plan = useMemo(
    () => (data?.ok ? data.data : [])?.find((plan) => plan.type === planType),
    [planType, data]
  );

  const upgrade = useCallback(() => {
    if (!plan) return;
    const { stripe_default_price_ids } = plan;

    const price_id =
      stripe_default_price_ids?.[currency.toLowerCase()] ??
      stripe_default_price_ids?.["usd"];

    createStripeCheckoutSession(price_id, plan.id, true)
      .then((response) => {
        if (response.data && response.ok) {
          window.location.href = response.data.url;
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
  }, [plan, currency]);

  return { isLoading, upgrade, plan, disabled: plan === undefined };
};

const GrowthPlanModalAE = ({ className, ...props }: { className?: string }) => {
  const { isLoading } = usePlanData();

  const { upgrade } = useStripeGrowthPlanUpgrade(PLAN_TYPE.US_BASIC);

  return (
    <Modal
      className={cx(styles.root, className)}
      contentLoading={isLoading}
      data-testid="growth-plan-modal"
      {...props}
    >
      <ModalContent className={styles.content}>
        <div className={styles.title}>
          <Typography variant="h2">{L.GROWTH_MODAL_TITLE}</Typography>
          <Typography variant="subtitle-1">
            {L.GROWTH_MODAL_SUBTITLE}
          </Typography>
        </div>

        <div className={styles.plans}>
          {[basic, custom1, custom2].map((plan) => (
            <PlanCard.Root plan={plan} key={plan.type}>
              <PlanCard.Name variant="subtitle-1" />
              <PlanCard.Price />
              <PlanCard.FeatureTitle />
              <PlanCard.Features />

              {plan?.type === PLAN_TYPE.US_BASIC ? (
                <PlanCard.CtaBasic
                  onClick={upgrade}
                  data-testid="growth-upgrade-to-basic"
                />
              ) : (
                <PlanCard.CtaCustom
                  planName={plan.name}
                  data-testid="growth-contact-sales"
                >
                  {L.GROWTH_CUSTOM_PLAN_CTA}
                </PlanCard.CtaCustom>
              )}
            </PlanCard.Root>
          ))}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default GrowthPlanModalAE;
